import { useState, useCallback, useMemo } from 'react';

/**
 * Manages merge mode, field selections, shipment selections, and selection summary
 * for the staging review modal.
 */
const useStagingMerge = ({ diffData }) => {
  const [mergeMode, setMergeMode] = useState('takeAll');
  const [fieldSelections, setFieldSelections] = useState({});
  const [shipmentSelections, setShipmentSelections] = useState({});
  const [shipmentFieldSelections, setShipmentFieldSelections] = useState({});

  const hasAnySelection = useMemo(() => {
    const hasField = Object.values(fieldSelections).some((v) => v === 'api');
    const hasShipment = Object.keys(shipmentSelections).length > 0;
    const hasShipmentField = Object.values(shipmentFieldSelections).some(
      (marketFields) => Object.values(marketFields).some((v) => v === 'api')
    );
    return hasField || hasShipment || hasShipmentField;
  }, [fieldSelections, shipmentSelections, shipmentFieldSelections]);

  const selectionSummary = useMemo(() => {
    let apiFieldCount = 0;
    let manualFieldCount = 0;
    if (diffData?.voyageFieldDiffs) {
      for (const diff of diffData.voyageFieldDiffs) {
        if (!diff.different) continue;
        const sel = fieldSelections[diff.fieldName] || 'manual';
        if (sel === 'api') apiFieldCount++;
        else manualFieldCount++;
      }
    }
    let shipApiCount = 0;
    let shipManualCount = 0;
    let shipExcludeCount = 0;
    let shipMixedApiFields = 0;
    let shipMixedManualFields = 0;
    if (diffData?.shipmentDiffs) {
      for (const group of diffData.shipmentDiffs) {
        const sel =
          shipmentSelections[group.marketId] ||
          (group.apiOnly ? 'INCLUDE_NEW' : 'KEEP_MANUAL');
        if (sel === 'EXCLUDE') {
          shipExcludeCount++;
          continue;
        }
        const perField = shipmentFieldSelections[group.marketId];
        const hasPerField =
          perField &&
          !group.apiOnly &&
          !group.manualOnly &&
          group.fieldDiffs?.some((d) => d.different);
        if (hasPerField) {
          for (const d of group.fieldDiffs) {
            if (!d.different) continue;
            const fSel = perField[d.fieldName] || 'manual';
            if (fSel === 'api') shipMixedApiFields++;
            else shipMixedManualFields++;
          }
        } else if (sel === 'TAKE_API' || sel === 'INCLUDE_NEW') {
          shipApiCount++;
        } else {
          shipManualCount++;
        }
      }
    }
    return {
      takingFromApi: apiFieldCount + shipApiCount + shipMixedApiFields,
      keepingManual: manualFieldCount + shipManualCount + shipMixedManualFields,
      excluding: shipExcludeCount,
    };
  }, [fieldSelections, shipmentSelections, shipmentFieldSelections, diffData]);

  const resetSelections = useCallback(() => {
    setMergeMode('takeAll');
    setFieldSelections({});
    setShipmentSelections({});
    setShipmentFieldSelections({});
  }, []);

  const handleFieldSelectionChange = (fieldName, value) => {
    setMergeMode('selective');
    setFieldSelections((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleShipmentSelectionChange = (marketId, action) => {
    setMergeMode('selective');
    setShipmentSelections((prev) => ({ ...prev, [marketId]: action }));
    if (action === 'TAKE_API' || action === 'KEEP_MANUAL') {
      setShipmentFieldSelections((prev) => {
        const next = { ...prev };
        delete next[marketId];
        return next;
      });
    }
  };

  const handleShipmentFieldSelectionChange = (marketId, fieldName, value) => {
    setMergeMode('selective');
    setShipmentFieldSelections((prev) => ({
      ...prev,
      [marketId]: { ...(prev[marketId] || {}), [fieldName]: value },
    }));
    setShipmentSelections((prev) => ({ ...prev, [marketId]: 'TAKE_API' }));
  };

  const setMergeModeValue = (val) => {
    if (!val) return;
    if (val === 'takeAll') {
      setFieldSelections({});
      setShipmentSelections({});
      setShipmentFieldSelections({});
    }
    setMergeMode(val);
  };

  return {
    mergeMode,
    fieldSelections,
    shipmentSelections,
    shipmentFieldSelections,
    hasAnySelection,
    selectionSummary,
    resetSelections,
    handleFieldSelectionChange,
    handleShipmentSelectionChange,
    handleShipmentFieldSelectionChange,
    setMergeModeValue,
  };
};

export default useStagingMerge;
